import React, { useState, useEffect, useRef, useMemo } from "react";
import styled, { keyframes, css } from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import temperatureLineIcon from "../../../../assets/images/weather_icon/temperature_line.png";
import windspeedLineIcon from "../../../../assets/images/weather_icon/windspeed_line.png";
import precipitationLineIcon from "../../../../assets/images/weather_icon/precipitation_line.png";
import humidityLineIcon from "../../../../assets/images/weather_icon/humidity_line.png";
// Light 테마용 아이콘들 추가
import windspeedLineLightIcon from "../../../../assets/images/weather_icon/windspeed_line_light.png";
import precipitationLineLightIcon from "../../../../assets/images/weather_icon/precipitation_line_light.png";
import humidityLineLightIcon from "../../../../assets/images/weather_icon/humidity_line_light.png";

import s30Icon from "../../../../assets/images/weather_icon/S30.png";
import s10Icon from "../../../../assets/images/weather_icon/S10.png";
import s40Icon from "../../../../assets/images/weather_icon/S40.png";
import s44Icon from "../../../../assets/images/weather_icon/S44.png";
import s45Icon from "../../../../assets/images/weather_icon/S45.png";
import s46Icon from "../../../../assets/images/weather_icon/S46.png";
import s47Icon from "../../../../assets/images/weather_icon/S47.png";
import s11Icon from "../../../../assets/images/weather_icon/S11.png";
import s12Icon from "../../../../assets/images/weather_icon/S12.png";
import s13Icon from "../../../../assets/images/weather_icon/S13.png";
import s14Icon from "../../../../assets/images/weather_icon/S14.png";
import s15Icon from "../../../../assets/images/weather_icon/S15.png";
import s16Icon from "../../../../assets/images/weather_icon/S16.png";
import s17Icon from "../../../../assets/images/weather_icon/S17.png";
import s31Icon from "../../../../assets/images/weather_icon/S31.png";
import s32Icon from "../../../../assets/images/weather_icon/S32.png";
import s33Icon from "../../../../assets/images/weather_icon/S33.png";
import s34Icon from "../../../../assets/images/weather_icon/S34.png";
import s35Icon from "../../../../assets/images/weather_icon/S35.png";
import s36Icon from "../../../../assets/images/weather_icon/S36.png";
import s37Icon from "../../../../assets/images/weather_icon/S37.png";
import s41Icon from "../../../../assets/images/weather_icon/S41.png";
import s42Icon from "../../../../assets/images/weather_icon/S42.png";
import s43Icon from "../../../../assets/images/weather_icon/S43.png";

import {
  getPlalionSensorData,
  getWeatherData,
  getWeatherForecastData,
  getOpenWeatherData,
  getWeatherDataInDB,
  viewCheckActivateStateLinkRequest,
  viewCheckUpdateStateLinkRequest,
} from "../../../../utils/indexedDB";
import AtomsGauge from "../atoms/AtomsGauge";
import { useAtom } from "jotai";
import {
  AUTODISPLAY_INTERVER_TIME,
  AUTODISPLAY_REACT_STATE,
  AUTODISPLAY_DISPLAY_WIDTH,
  AUTODISPLAY_DISPLAY_HEIGHT,
  AUTODISPLAY_SELECTED_BACKGROUND,
} from "../../../AtomStates";

const marqueeAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-100% - 4em));
  }
`;

const thermoLineIcon = temperatureLineIcon;

// 날씨 아이콘 매핑 객체
const weatherIcons = {
  // 기상청 아이콘
  S10: s10Icon,
  S11: s11Icon,
  S12: s12Icon,
  S13: s13Icon,
  S14: s14Icon,
  S15: s15Icon,
  S16: s16Icon,
  S17: s17Icon,
  S30: s30Icon,
  S31: s31Icon,
  S32: s32Icon,
  S33: s33Icon,
  S34: s34Icon,
  S35: s35Icon,
  S36: s36Icon,
  S37: s37Icon,
  S40: s40Icon,
  S41: s41Icon,
  S42: s42Icon,
  S43: s43Icon,
  S44: s44Icon,
  S45: s45Icon,
  S46: s46Icon,
  S47: s47Icon,

  // OpenWeather 아이콘
  '01d': s10Icon, // 맑음 낮
  '01n': s10Icon, // 맑음 밤
  '02d': s30Icon, // 구름 조금 낮
  '02n': s30Icon, // 구름 조금 밤
  '03d': s40Icon, // 구름 많음 낮
  '03n': s40Icon, // 구름 많음 밤
  '04d': s40Icon, // 구름 매우 많음 낮
  '04n': s40Icon, // 구름 매우 많음 밤
  '09d': s41Icon, // 소나기 낮
  '09n': s41Icon, // 소나기 밤
  '10d': s31Icon, // 비 낮
  '10n': s31Icon, // 비 밤
  '11d': s40Icon, // 뇌우 낮
  '11n': s40Icon, // 뇌우 밤
  '13d': s43Icon, // 눈 낮
  '13n': s43Icon, // 눈 밤
  '50d': s40Icon, // 안개 낮
  '50n': s40Icon, // 안개 밤
};

// 아이콘 코드로 실제 아이콘 이미지 가져오기
const getWeatherIconImage = (iconCode) => {
  return weatherIcons[iconCode] || s10Icon;
};

// Helper for responsive units - 기본 스케일링 (가로 기준) 순수 비율만
const r = (value) => {
  const baseWidth = 1920;
  const baseHeight = 1080;

  return `min(${(value / baseWidth) * 100}vw, ${(value / baseHeight) * 100}vh)`;
};

// Helper for responsive units - 세로 비율이 긴 경우 VH 기준 스케일링 순수 비율만
const rv = (value) => {
  const baseHeight = 1080;

  return `${(value / baseHeight) * 100}vh`;
};

// 테마별 컬러 매핑 객체
const themeColors = {
  dark: {
    mainColor: "white",
    MainContainerBackgroundLinearGradient01: "rgba(0, 0, 0, 0.4)",
    MainContainerBackgroundLinearGradient02: "rgba(0, 0, 0, 0.4)",
    MainPanelBackgroundLinearGradient01: "rgba(0, 0, 0, 0.3)",
    MainPanelBackgroundLinearGradient02: "rgba(0, 0, 0, 0.5)",
    MainPanelBackgroundLinearGradient03: "rgba(0, 0, 0, 0.3)",
    MainPanelOutline: "rgba(255, 255, 255, 0.4)",
    WeatherPanelBackgroundLinearGradient01: "rgba(0, 0, 0, 0.3)",
    WeatherPanelBackgroundLinearGradient02: "rgba(0, 0, 0, 0.5)",
    WeatherPanelBackgroundLinearGradient03: "rgba(0, 0, 0, 0.3)",
    WeatherPanelOutline: "rgba(255, 255, 255, 0.4)",
    WeatherLabelColor: "rgba(255, 255, 255, 0.6)",
    bottomMessageBackground: "rgba(255, 255, 255, 0.2)",
  },
  light: {
    mainColor: "black",
    MainContainerBackgroundLinearGradient01: "rgba(255, 255, 255, 0.4)",
    MainContainerBackgroundLinearGradient02: "rgba(255, 255, 255, 0.4)",
    MainPanelBackgroundLinearGradient01: "rgba(255, 255, 255, 0.3)",
    MainPanelBackgroundLinearGradient02: "rgba(255, 255, 255, 0.5)",
    MainPanelBackgroundLinearGradient03: "rgba(255, 255, 255, 0.3)",
    MainPanelOutline: "rgba(255, 255, 255, 0.4)",
    WeatherPanelBackgroundLinearGradient01: "rgba(255, 255, 255, 0.3)",
    WeatherPanelBackgroundLinearGradient02: "rgba(255, 255, 255, 0.5)",
    WeatherPanelBackgroundLinearGradient03: "rgba(255, 255, 255, 0.3)",
    MainPanelOutline: "rgba(255, 255, 255, 0.4)",
    WeatherLabelColor: "rgba(0, 0, 0, 0.6)",
    bottomMessageBackground: "rgba(0, 0, 0, 0.2)",
  },
};

/**
 * 테마모드에 따른 색상을 가져오는 함수
 * @param {string} colorName - 색상 이름 (예: "mainColor", "MainPanelOutline")
 * @param {string} themeMode - 테마 모드 ("dark" 또는 "light")
 * @returns {string} 해당 테마의 색상 값
 */
const getThemeColor = (colorName, themeMode = "dark") => {
  const theme = themeColors[themeMode] || themeColors.dark;
  return theme[colorName] || themeColors.dark[colorName];
};

/**
 * 테마모드에 따른 날씨 아이콘을 가져오는 함수
 * @param {string} iconType - 아이콘 타입 ("windspeed", "precipitation", "humidity")
 * @param {string} themeMode - 테마 모드 ("dark" 또는 "light")
 * @returns {string} 해당 테마의 아이콘 경로
 */
const getWeatherThemeIcon = (iconType, themeMode = "dark") => {
  const weatherIcons = {
    dark: {
      windspeed: windspeedLineIcon,
      precipitation: precipitationLineIcon,
      humidity: humidityLineIcon,
    },
    light: {
      windspeed: windspeedLineLightIcon,
      precipitation: precipitationLineLightIcon,
      humidity: humidityLineLightIcon,
    },
  };

  const theme = weatherIcons[themeMode] || weatherIcons.dark;
  return theme[iconType] || weatherIcons.dark[iconType];
};

// 배경 이미지는 사용하지 않음 (단색 배경 적용)

const OrganismDisplayViewLinkLotation = ({
  viewId,
  textData: initialTextData,
  sensorSerialData: initialSensorSerialData,
  locationData: initialLocationData,
  themeMode: initialThemeMode, // "dark" or "light"
  weatherData: initialWeatherData,
  backgroundImage,
}) => {
  const navigate = useNavigate();

  // atom에서 해상도 및 반응형 모드 정보 가져오기
  const [reactState] = useAtom(AUTODISPLAY_REACT_STATE);
  const [displayWidth] = useAtom(AUTODISPLAY_DISPLAY_WIDTH);
  const [displayHeight] = useAtom(AUTODISPLAY_DISPLAY_HEIGHT);
  const [selectedBackgroundIndex, setSelectedBackgroundIndex] = useAtom(
    AUTODISPLAY_SELECTED_BACKGROUND
  );

  // 고정 모드 조건을 더 명확하게 체크
  const isFixedMode =
    reactState === "fixed" && displayWidth > 0 && displayHeight > 0;

  // 반응형 모드에서는 기존과 완전히 동일한 초기값 사용
  const [orientation, setOrientation] = useState("landscape");
  const [isWider, setIsWider] = useState(false);
  const [isTaller, setIsTaller] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  const messageContainerRef = useRef(null);
  const messageTextRef = useRef(null);

  // 백그라운드 선택 팝업 표시 상태
  const [showBackgroundSelector, setShowBackgroundSelector] = useState(false);

  // 기존 props를 useState로 관리
  const [textData, setTextData] = useState(initialTextData || "Loading...");
  const [sensorSerialData, setSensorSerialData] = useState(
    initialSensorSerialData
  );
  const [locationData, setLocationData] = useState(initialLocationData);
  const [themeMode, setThemeMode] = useState(initialThemeMode || "light");

  const [weatherData, setWeatherData] = useState(initialWeatherData || {
    temperature: "-",
    wind: "-",
    precipitation: "-",
    humidity: "-",
    pty: "-", // 강수형태 추가
  });
  const [sensorData, setSensorData] = useState({
    ozone: "0",
    temperature: "23",
    humidity: "55",
    dust: "0",
    voc: "3",
    co2: "403",
  });
  const lastWeatherUpdateHourRef = useRef(null);

  // 예보 데이터 state 추가
  const [weatherForecastData, setWeatherForecastData] = useState(initialWeatherData || {
    maxTemperature: "-",
    minTemperature: "-",
    weatherMain: "-",
  });
  const [lastTempUpdateDate, setLastTempUpdateDate] = useState(null); // TMX, TMN 마지막 업데이트 날짜
  const lastWeatherFetchMinuteRef = useRef(null); // 마지막 날씨 호출 분 스탬프

  const [autodisplayIntervalTime] = useAtom(AUTODISPLAY_INTERVER_TIME);

  // props 동기화: 상위에서 콘텐츠가 변경되면 내부 상태 갱신
  useEffect(() => {
    setTextData(initialTextData || "");
  }, [initialTextData]);

  useEffect(() => {
    setSensorSerialData(initialSensorSerialData || null);
  }, [initialSensorSerialData]);

  useEffect(() => {
    setLocationData(initialLocationData || null);
  }, [initialLocationData]);

  useEffect(() => {
    setThemeMode(initialThemeMode || "light");
  }, [initialThemeMode]);

  useEffect(() => {
    const checkOverflow = () => {
      const container = messageContainerRef.current;
      const text = messageTextRef.current;
      if (container && text) {
        const isOverflow = text.scrollWidth > container.clientWidth;
        if (isOverflow !== isTextOverflowing) {
          setIsTextOverflowing(isOverflow);
        }
      }
    };

    const timer = setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [textData, orientation, isWider, isTaller, isTextOverflowing]);

  const fetchSensorData = async () => {
    // sensorSerialData가 없거나 serialCode가 없으면 함수 종료
    if (!sensorSerialData || !sensorSerialData.serialCode) {
      console.log("센서 시리얼 데이터가 없어서 센서 데이터 요청을 건너뜁니다.");
      setSensorData({
        ozone: "-",
        temperature: "-",
        humidity: "-",
        dust: "-",
        voc: "-",
        co2: "-",
      });
      return;
    }

    try {
      const response = await getPlalionSensorData({
        serial_num: sensorSerialData.serialCode,
      });
      // console.log(response);
      if (
        response &&
        response.code === 101 &&
        response.rows &&
        response.rows.length > 0
      ) {
        const data = response.rows[0];

        setSensorData({
          ozone: parseFloat(data.ozone_val).toFixed(3),
          temperature: parseFloat(data.temp_val),
          humidity: parseFloat(data.humi_val),
          dust: parseFloat(data.dust_val),
          voc: parseFloat(data.voc_val),
          co2: parseFloat(data.co2_val),
        });
      } else {
        // console.log("Sensor data not available:", response);
        setSensorData({
          ozone: "-",
          temperature: "-",
          humidity: "-",
          dust: "-",
          voc: "-",
          co2: "-",
        });
      }
    } catch (error) {
      console.error("Error fetching sensor data:", error);
      setSensorData({
        ozone: "-",
        temperature: "-",
        humidity: "-",
        dust: "-",
        voc: "-",
        co2: "-",
      });
    }
  };

  const fetchWeatherData = async () => {
    // locationData가 유효한지 확인
    if (
      !locationData?.selectedDistricts?.x ||
      !locationData?.selectedDistricts?.y
    ) {
      console.log(
        "🚀 ~ fetchWeatherData ~ locationData가 유효하지 않음:",
        locationData
      );
      return;
    }

    const now = new Date();
    let base_date_obj = new Date();

    let currentHour = now.getHours();
    let base_time = "";

    // 초단기실황 API는 매시 45분 이후에 데이터가 제공됩니다.
    // 안전하게 45분 미만일 경우 이전 시간의 데이터를 요청합니다.
    if (now.getMinutes() < 45) {
      // 현재 시간이 0시이고 45분 미만이면, 전날 23시 데이터를 요청합니다.
      if (currentHour === 0) {
        base_date_obj.setDate(base_date_obj.getDate() - 1);
        base_time = "2300";
      } else {
        // 0시가 아니면, 현재 시간 -1 시간으로 요청합니다.
        base_time = `${String(currentHour - 1).padStart(2, "0")}00`;
      }
    } else {
      // 45분 이상이면 현재 시간으로 데이터를 요청합니다.
      base_time = `${String(currentHour).padStart(2, "0")}00`;
    }

    const base_date = `${base_date_obj.getFullYear()}${String(
      base_date_obj.getMonth() + 1
    ).padStart(2, "0")}${String(base_date_obj.getDate()).padStart(2, "0")}`;

    const nx = locationData?.selectedDistricts?.x;
    const ny = locationData?.selectedDistricts?.y;

    const params = {
      pageNo: 1,
      numOfRows: 10,
      dataType: "JSON",
      base_date: base_date,
      base_time: base_time,
      nx,
      ny,
    };
    console.log("🚀 ~ fetchWeatherData ~ params:", params);

    try {
      const response = await getWeatherData(params);

      if (response.data.response.header.resultCode === "00") {
        const items = response.data.response.body.items.item;
        const weather = {};
        items.forEach((item) => {
          switch (item.category) {
            case "T1H": // 기온
              weather.temperature = item.obsrValue;
              break;
            case "RN1": // 1시간 강수량
              weather.precipitation = item.obsrValue;
              break;
            case "WSD": // 풍속
              weather.wind = item.obsrValue;
              break;
            case "REH": // 습도
              weather.humidity = item.obsrValue;
              break;
            case "PTY": // 강수형태
              weather.pty = item.obsrValue;
              break;
            default:
              break;
          }
        });
        setWeatherData((prevData) => ({ ...prevData, ...weather }));
      } else {
        console.error(
          "Weather API Error:",
          response.data.response.header.resultMsg
        );
      }
    } catch (error) {
      console.error("Failed to fetch weather data:", error);
      throw error;
    }
  };

  // 날씨 예보 API 파라미터 계산 함수
  const calculateForecastParams = (currentTime) => {
    const year = currentTime.getFullYear();
    const month = String(currentTime.getMonth() + 1).padStart(2, "0");
    const day = String(currentTime.getDate()).padStart(2, "0");
    const hour = currentTime.getHours();

    // API 발표 시간 기준
    let baseTime;
    if (hour >= 23) {
      baseTime = "2300";
    } else if (hour >= 20) {
      baseTime = "2000";
    } else if (hour >= 17) {
      baseTime = "1700";
    } else if (hour >= 14) {
      baseTime = "1400";
    } else if (hour >= 11) {
      baseTime = "1100";
    } else if (hour >= 8) {
      baseTime = "0800";
    } else if (hour >= 5) {
      baseTime = "0500";
    } else {
      baseTime = "0200";
    }

    // 새벽 시간대는 전날 데이터 사용
    let baseDate;
    if (hour < 2) {
      const yesterday = new Date(currentTime);
      yesterday.setDate(yesterday.getDate() - 1);
      baseDate = `${yesterday.getFullYear()}${String(
        yesterday.getMonth() + 1
      ).padStart(2, "0")}${String(yesterday.getDate()).padStart(2, "0")}`;
      baseTime = "2300";
    } else {
      baseDate = `${year}${month}${day}`;
    }

    return {
      pageNo: 1,
      base_date: baseDate,
      base_time: baseTime,
    };
  };

  // 날씨 예보 데이터 가져오기
  const fetchWeatherForecast = async () => {
    // locationData가 유효한지 확인
    if (
      !locationData?.selectedDistricts?.x ||
      !locationData?.selectedDistricts?.y
    ) {
      console.log(
        "🚀 ~ fetchWeatherForecast ~ locationData가 유효하지 않음:",
        locationData
      );
      return;
    }

    try {
      const now = new Date();
      const params = calculateForecastParams(now);

      const forecastParams = {
        pageNo: params.pageNo,
        numOfRows: 12,
        dataType: "JSON",
        base_date: params.base_date,
        base_time: params.base_time,
        nx: locationData?.selectedDistricts?.x, // 지역별 X 좌표
        ny: locationData?.selectedDistricts?.y, // 지역별 Y 좌표
      };

      const response = await getWeatherForecastData(forecastParams);
      console.log(`예보 데이터 (${now.getHours()}시):`, params, response);

      if (response.data.response.header.resultCode === "00") {
        const items = response.data.response.body.items.item;
        const forecast = {};

        items.forEach((item) => {
          switch (item.category) {
            case "SKY": // 하늘 상태
              forecast.sky = item.fcstValue;
              break;
            // TMX, TMN은 별도 함수에서 처리하므로 제거
          }
        });

        setWeatherForecastData((prev) => ({ ...prev, ...forecast }));
      }

      // 임시 데이터 (개발용)
      console.log(`예보 API 파라미터 (${now.getHours()}시):`, params);
    } catch (error) {
      console.error("날씨 예보 데이터 가져오기 실패:", error);
      throw error;
    }
  };

  // TMX, TMN 데이터 가져오기 (매일 02시 기준)
  const fetchTemperatureMinMax = async () => {
    // locationData가 유효한지 확인
    if (
      !locationData?.selectedDistricts?.x ||
      !locationData?.selectedDistricts?.y
    ) {
      console.log(
        "🚀 ~ fetchTemperatureMinMax ~ locationData가 유효하지 않음:",
        locationData
      );
      return;
    }

    try {
      const now = new Date();
      const currentDate = `${now.getFullYear()}${String(
        now.getMonth() + 1
      ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;

      // 이미 오늘 업데이트했다면 스킵
      if (lastTempUpdateDate === currentDate) {
        console.log("TMX, TMN 데이터는 이미 오늘 업데이트됨");
        return;
      }

      const tempParams = {
        pageNo: 1,
        numOfRows: 300,
        dataType: "JSON",
        base_date: currentDate,
        base_time: "0200",
        nx: locationData?.selectedDistricts?.x,
        ny: locationData?.selectedDistricts?.y,
      };

      console.log("TMX, TMN 데이터 요청:", tempParams);
      const response = await getWeatherForecastData(tempParams);

      if (response.data.response.header.resultCode === "00") {
        const items = response.data.response.body.items.item;
        const tempData = {};

        // 오늘 날짜의 TMX, TMN 찾기
        items.forEach((item) => {
          if (item.fcstDate === currentDate) {
            switch (item.category) {
              case "TMX": // 최고 기온
                tempData.maxTemperature = item.fcstValue;
                break;
              case "TMN": // 최저 기온
                tempData.minTemperature = item.fcstValue;
                break;
            }
          }
        });

        // 데이터가 있으면 업데이트
        if (tempData.maxTemperature || tempData.minTemperature) {
          setWeatherForecastData((prev) => ({ ...prev, ...tempData }));
          setLastTempUpdateDate(currentDate);
          console.log("TMX, TMN 데이터 업데이트 완료:", tempData);
        } else {
          console.log("오늘 날짜의 TMX, TMN 데이터를 찾을 수 없음");
        }
      }
    } catch (error) {
      console.error("TMX, TMN 데이터 가져오기 실패:", error);
      throw error;
    }
  };

  // 현재 온도와 최대/최저 온도 비교하여 업데이트
  useEffect(() => {
    if (
      weatherData.temperature &&
      (weatherForecastData.maxTemperature || weatherForecastData.minTemperature)
    ) {
      const currentTemp = parseFloat(weatherData.temperature);
      let shouldUpdate = false;
      let updateData = {};

      // 최대 온도 체크
      if (weatherForecastData.maxTemperature) {
        const maxTemp = parseFloat(weatherForecastData.maxTemperature);
        if (currentTemp > maxTemp) {
          console.log(
            `현재 온도(${currentTemp}°C)가 최대 온도(${maxTemp}°C)보다 높아서 최대 온도를 업데이트합니다.`
          );
          updateData.maxTemperature = weatherData.temperature;
          shouldUpdate = true;
        }
      }

      // 최저 온도 체크
      if (weatherForecastData.minTemperature) {
        const minTemp = parseFloat(weatherForecastData.minTemperature);
        if (currentTemp < minTemp) {
          console.log(
            `현재 온도(${currentTemp}°C)가 최저 온도(${minTemp}°C)보다 낮아서 최저 온도를 업데이트합니다.`
          );
          updateData.minTemperature = weatherData.temperature;
          shouldUpdate = true;
        }
      }

      if (shouldUpdate) {
        setWeatherForecastData((prev) => ({
          ...prev,
          ...updateData,
        }));
      }
    }
  }, [
    weatherData.temperature,
    weatherForecastData.maxTemperature,
    weatherForecastData.minTemperature,
  ]);

  // OpenWeather API를 사용한 날씨 데이터 가져오기
  const fetchOpenWeatherData = async () => {
    try {
      const lat = locationData?.selectedDistricts?.lat;
      const lon = locationData?.selectedDistricts?.lon;

      if (!lat || !lon) {
        console.error("위치 정보가 없습니다.");
        return;
      }

      const params = {
        lat: lat,
        lon: lon,
      };

      const response = await getOpenWeatherData(params);

      if (response.data) {
        const data = response.data;

        // 현재 날씨 데이터 (One Call API)
        const weather = {
          temperature: Math.round(data.current.temp), // 현재 기온
          wind: data.current.wind_speed ? data.current.wind_speed.toFixed(1) : "0", // 풍속
          humidity: data.current.humidity, // 습도
          precipitation: data.current.rain ? (data.current.rain['1h'] || 0) : 0, // 강수량 (1시간)
        };

        // 일일 예보 데이터 (최저/최고 기온, 날씨 상태)
        const forecast = {
          minTemperature: Math.round(data.daily[0].temp.min),
          maxTemperature: Math.round(data.daily[0].temp.max),
          weatherMain: data.current.weather ? data.current.weather[0].main : "Clear", 
          weatherIcon: data.current.weather ? data.current.weather[0].icon : "01d",
        };

        setWeatherData(weather);
        setWeatherForecastData(forecast);
      }
    } catch (error) {
      console.error("OpenWeather 데이터 가져오기 실패:", error);
    }
  };

  const fetchWeatherDataInDB = async () => {
    try {
      const params = { "x": locationData?.selectedDistricts?.x, "y": locationData?.selectedDistricts?.y };
      const res = await getWeatherDataInDB(params);
      const data = res?.data?.data;
      if (data) {
        setWeatherData((prev) => ({
          ...prev,
          temperature: Math.round(data.temp),
          wind: data.wsd,
          humidity: Math.round(data.humidity),
          precipitation: data.rain_1h,
        }));
        setWeatherForecastData((prev) => ({
          ...prev,
          maxTemperature: Math.round(data.temp_max),
          minTemperature: Math.round(data.temp_min),
          weatherMain: data.weather_main,
          weatherIcon: data.weather_icon,
        }));
      }
    } catch (error) {
      console.error("WeatherDataInDB 데이터 가져오기 실패:", error);
      fetchOpenWeatherData();
    }
  };

  // 동일 분 중복 호출 방지용 래퍼
  const requestWeatherUpdate = async (reason = "") => {
    const key = currentLocationKeyRef.current;
    if (!key) return;
    const now = new Date();
    const minuteStamp = `${key}|${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}-${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
    if (lastWeatherFetchMinuteRef.current === minuteStamp) {
      return; // 같은 분에 이미 처리함
    }
    lastWeatherFetchMinuteRef.current = minuteStamp;
    try {
      // await fetchWeatherData();
      await fetchWeatherDataInDB();
    } catch (error) {
      console.error("requestWeatherUpdate: fetchWeatherData failed:", error);
      // await fetchOpenWeatherData();
    }
    // try {
    //   await fetchWeatherForecast();
    // } catch (error) {
    //   console.error("requestWeatherUpdate: fetchWeatherForecast failed:", error);
    //   await fetchOpenWeatherData();
    // }
  };

  // 좌표가 실제로 바뀐 경우에만 초기 로딩
  const locationKey = useMemo(() => {
    const x = locationData?.selectedDistricts?.x;
    const y = locationData?.selectedDistricts?.y;
    return x && y ? `${x},${y}` : null;
  }, [locationData?.selectedDistricts?.x, locationData?.selectedDistricts?.y]);

  const lastFetchedLocationKeyRef = useRef(null);
  const currentLocationKeyRef = useRef(null);

  // 최신 좌표를 ref에 유지 (interval 콜백에서 사용)
  useEffect(() => {
    currentLocationKeyRef.current = locationKey;
  }, [locationKey]);

  // 좌표가 바뀌었을 때만 초기 데이터 로드
  useEffect(() => {
    const load = async () => {
      if (!locationKey) {
        console.log("🚀 ~ useEffect ~ locationData 유효하지 않음:", locationData);
        return;
      }
      // 외부 날씨를 우선 사용하도록 지정되면 내부 최초 호출도 생략
      if (initialWeatherData != null) {
        lastFetchedLocationKeyRef.current = locationKey;
        return;
      }
      if (lastFetchedLocationKeyRef.current === locationKey) return; // 동일 좌표면 재호출 방지
      await requestWeatherUpdate("initial");
      // try {
      //   await fetchTemperatureMinMax();
      // } catch (error) {
      //   console.error("fetchTemperatureMinMax failed:", error);
      //   fetchOpenWeatherData();
      // }
      lastFetchedLocationKeyRef.current = locationKey;
    };
    load();
  }, [locationKey]);

  // 타이머는 한 번만 설정 (mount/unmount)
  useEffect(() => {
    // 시간 업데이트: 1초마다 실행 (날씨 데이터 여부와 무관하게 항상 실행)
    const dateTimerId = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);

    // 날씨 데이터를 prop으로 받았을 경우 날씨 타이머 설정 안함
    if (initialWeatherData != null) {
      return () => clearInterval(dateTimerId);
    }

    const weatherTimerId = setInterval(() => {
      const now = new Date();
      const currentHour = now.getHours();
      const key = currentLocationKeyRef.current;
      // 매시간 정각에만 호출 (배포용) - 좌표가 존재할 때만
      if (now.getMinutes() === 0 && key) {
        requestWeatherUpdate("hourly-00");
        // if (currentHour === 2) {
        //   fetchTemperatureMinMax();
        // }
      }
    }, 60000); // 1분마다 체크

    return () => {
      clearInterval(weatherTimerId);
      clearInterval(dateTimerId);
    };
  }, []);

  // 센서 데이터를 위한 별도 useEffect
  useEffect(() => {
    fetchSensorData(); // 초기 호출

    // 센서 데이터가 있을 때만 타이머 설정
    let sensorTimerId;
    if (sensorSerialData && sensorSerialData.serialCode) {
      sensorTimerId = setInterval(() => {
        fetchSensorData();
      }, 10 * 6 * 1000); // 60초마다 센서 데이터 업데이트
    }

    return () => {
      if (sensorTimerId) {
        clearInterval(sensorTimerId);
      }
    };
  }, [sensorSerialData]); // sensorSerialData가 변경될 때마다 실행

  // 고정 모드일 때만 실행되는 useEffect
  useEffect(() => {
    if (reactState === "fixed" && displayWidth > 0 && displayHeight > 0) {
      const fixedRatio = displayWidth / displayHeight;
      setOrientation(fixedRatio > 1 ? "landscape" : "portrait");
      setIsWider(fixedRatio > 16 / 9);
      setIsTaller(fixedRatio < 9 / 16);
    }
  }, [reactState, displayWidth, displayHeight]);

  // 반응형 모드일 때만 실행되는 useEffect (기존과 완전히 동일)
  useEffect(() => {
    // reactState가 'active'이거나 undefined일 때만 실행 (기존 동작)
    if (reactState === "active" || reactState === undefined) {
      const handleResize = () => {
        const currentRatio = window.innerWidth / window.innerHeight;
        setOrientation(currentRatio > 1 ? "landscape" : "portrait");
        setIsWider(currentRatio > 16 / 9);
        setIsTaller(currentRatio < 9 / 16);
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []); // 기존과 완전히 동일한 빈 배열

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const day = currentDate.getDate();
  const hours = String(currentDate.getHours()).padStart(2, "0");
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");

  // console.log("PageAutodisplayViewApi 컴포넌트가 렌더링됩니다.");

  // 날씨 상태에 따른 텍스트
  const getWeatherStatus = (weather) => {
    switch (weather) {
      // 기상청
      case "1":
        return "맑음";
      case "3":
        return "구름 많음";
      case "4":
        return "흐림";

      // OpenWeather
      case "Clear":
        return "맑음";
      case "Clouds":
        return "구름 많음";
      case "Rain":
        return "비";
      case "Drizzle":
        return "이슬비";
      case "Thunderstorm":
        return "뇌우";
      case "Snow":
        return "눈";
      case "Mist":
      case "Fog":
        return "안개";
      default:
        if (typeof weather === "string") {
          return weather;
        }
        return "-";
    }
  };

  // SKY와 PTY 값을 조합하여 날씨 아이콘 코드 생성
  const getWeatherIconCode = (skyCode, ptyCode) => {
    // 아이콘 파일명: S + SKY값 + PTY값
    // 예: 맑음(SKY=1) + 없음(PTY=0) = S10
    // 예: 흐림(SKY=4) + 비/눈(PTY=2) = S42
    return `S${skyCode}${ptyCode}`;
  };

  // 현재 날씨 아이콘 (OpenWeather icon 코드 사용)
  const currentWeatherIcon = weatherForecastData.sky && weatherForecastData.pyt ? 
    getWeatherIconCode(
      weatherForecastData.sky,
      weatherData.pty
    ) : weatherForecastData.weatherIcon;

  // 체감온도 계산 함수
  const calculateFeelsLike = (temperature, humidity, windSpeed) => {
    const temp = parseFloat(temperature);
    const humid = parseFloat(humidity);
    const wind = parseFloat(windSpeed);

    // 온도가 26.7°C 이상일 때: Heat Index (열 지수) 사용
    if (temp >= 26.7) {
      // 섭씨를 화씨로 변환
      const tempF = (temp * 9) / 5 + 32;

      // Heat Index 계산 (화씨 기준)
      const c1 = -42.379;
      const c2 = 2.04901523;
      const c3 = 10.14333127;
      const c4 = -0.22475541;
      const c5 = -0.00683783;
      const c6 = -0.05481717;
      const c7 = 0.00122874;
      const c8 = 0.00085282;
      const c9 = -0.00000199;

      const heatIndexF =
        c1 +
        c2 * tempF +
        c3 * humid +
        c4 * tempF * humid +
        c5 * tempF * tempF +
        c6 * humid * humid +
        c7 * tempF * tempF * humid +
        c8 * tempF * humid * humid +
        c9 * tempF * tempF * humid * humid;

      // 화씨를 섭씨로 변환
      const heatIndexC = ((heatIndexF - 32) * 5) / 9;
      return Math.round(heatIndexC);
    }

    // 온도가 10°C 이하일 때: Wind Chill (바람차가움 지수) 사용
    else if (temp <= 10) {
      // 풍속을 m/s에서 km/h로 변환
      const windKmh = wind * 3.6;

      // Wind Chill 계산 (풍속이 4.8km/h 이상일 때만 적용)
      if (windKmh >= 4.8) {
        const windChill =
          13.12 +
          0.6215 * temp -
          11.37 * Math.pow(windKmh, 0.16) +
          0.3965 * temp * Math.pow(windKmh, 0.16);
        return Math.round(windChill);
      } else {
        // 바람이 약할 때는 실제 온도 반환
        return Math.round(temp);
      }
    }

    // 중간 온도대 (10°C ~ 26.7°C): 습도 기반 보정
    else {
      let adjustment = 0;

      // 습도가 높을 때 (70% 이상) 더 덥게 느껴짐
      if (humid >= 70) {
        adjustment = (humid - 70) * 0.1; // 습도 10%당 1도씩 증가
      }
      // 습도가 낮을 때 (30% 이하) 더 시원하게 느껴짐
      else if (humid <= 30) {
        adjustment = (30 - humid) * -0.05; // 습도 10%당 0.5도씩 감소
      }

      // 바람 효과 (풍속 1m/s당 약 0.5도 시원하게)
      const windAdjustment = wind * -0.5;

      const feelsLike = temp + adjustment + windAdjustment;
      return Math.round(feelsLike);
    }
  };

  // 체감온도 계산
  const feelsLikeTemperature = calculateFeelsLike(
    weatherData.temperature,
    weatherData.humidity,
    weatherData.wind
  );

  const textContent =
    textData || "실내 환경과 날씨 정보를 실시간으로 확인하세요.";

  return (
    <MainContainer
      orientation={orientation}
      isFixedMode={isFixedMode}
      displayWidth={displayWidth}
      displayHeight={displayHeight}
      themeMode={themeMode}
      backgroundImage={backgroundImage}
    >
      {/* 백그라운드 선택 버튼 */}
      {/* <BackgroundSelectorButton
        onClick={() => setShowBackgroundSelector(!showBackgroundSelector)}
        themeMode={themeMode}
      >
        🖼️
      </BackgroundSelectorButton> */}

      {/* 백그라운드 선택 팝업 제거 (단색 배경 적용) */}

      <ContentWrapper>
        {/* 메인 패널 */}
        <MainPanel orientation={orientation} themeMode={themeMode}>
          {/* 헤더 */}
          <HeaderContainer orientation={orientation}>
            <TitleSection>
              <MainTitle themeMode={themeMode}>
                {sensorSerialData?.title || "Auto Display"}
              </MainTitle>
            </TitleSection>
            <DateTimeSection orientation={orientation}>
              <DateContainer>
                <DateRow>
                  <DateText themeMode={themeMode}>{month}월</DateText>
                  <DateText themeMode={themeMode}>{day}일</DateText>
                </DateRow>
                <TimeText themeMode={themeMode}>
                  {hours}:{minutes}
                </TimeText>
              </DateContainer>
            </DateTimeSection>
          </HeaderContainer>

          {/* 센서 그리드 */}
          <SensorGrid orientation={orientation}>
            <SensorItem>
              <GaugeWrapper>
                <AtomsGauge
                  value={
                    sensorData.temperature === "-" ? 0 : sensorData.temperature
                  }
                  unit="°C"
                  label="온도"
                  minValue={-10}
                  maxValue={100}
                  goodRange={[15, 32]}
                  normalRange={[-10, 14]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper>
                <AtomsGauge
                  value={sensorData.ozone === "-" ? 0 : sensorData.ozone}
                  unit="ppm"
                  label="오존"
                  minValue={0}
                  maxValue={0.2}
                  goodRange={[0, 0.03]}
                  normalRange={[0.03, 0.06]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper>
                <AtomsGauge
                  value={sensorData.humidity === "-" ? 0 : sensorData.humidity}
                  unit="%"
                  label="습도"
                  minValue={0}
                  maxValue={100}
                  goodRange={[40, 70]}
                  normalRange={[0, 39]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper>
                <AtomsGauge
                  value={sensorData.dust === "-" ? 0 : sensorData.dust}
                  unit="㎍/㎥"
                  label="미세먼지"
                  minValue={0}
                  maxValue={150}
                  goodRange={[0, 30]}
                  normalRange={[31, 80]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper>
                <AtomsGauge
                  value={sensorData.voc === "-" ? 0 : sensorData.voc}
                  unit="ppm"
                  label="VOC"
                  minValue={0}
                  maxValue={1000}
                  goodRange={[0, 200]}
                  normalRange={[201, 500]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
            <SensorSeparator themeMode={themeMode} />
            <SensorItem>
              <GaugeWrapper>
                <AtomsGauge
                  value={sensorData.co2 === "-" ? 0 : sensorData.co2}
                  unit="ppm"
                  label="이산화탄소"
                  minValue={0}
                  maxValue={3000}
                  goodRange={[0, 800]}
                  normalRange={[801, 1500]}
                  showStatus={true}
                  themeMode={themeMode}
                />
              </GaugeWrapper>
            </SensorItem>
          </SensorGrid>
        </MainPanel>

        {/* 날씨 패널 */}
        <WeatherPanel themeMode={themeMode}>
          <NewWeatherContentWrapper orientation={orientation}>
            {/* 첫 번째 묶음: 메인 날씨 정보 */}
            <MainWeatherGroup>
              <MainWeatherIcon
                src={getWeatherIconImage(currentWeatherIcon)}
                alt={getWeatherStatus(weatherForecastData.sky ? weatherForecastData.sky : weatherForecastData.weatherMain)}
              />
              <WeatherInfoContainer>
                <WeatherStatus themeMode={themeMode}>
                  {getWeatherStatus(weatherForecastData.sky ? weatherForecastData.sky : weatherForecastData.weatherMain)}
                </WeatherStatus>
                <MainTemperatureContainer>
                  <MainTemperatureNumber themeMode={themeMode}>
                    {weatherData.temperature}
                  </MainTemperatureNumber>
                  <MainTemperatureUnit themeMode={themeMode}>
                    °C
                  </MainTemperatureUnit>
                </MainTemperatureContainer>
              </WeatherInfoContainer>
              <MinMaxTemperatureContainer>
                <MinMaxTemperature themeMode={themeMode}>
                  최저 {weatherForecastData.minTemperature}°
                </MinMaxTemperature>
                <MinMaxTemperature themeMode={themeMode}>
                  최고 {weatherForecastData.maxTemperature}°
                </MinMaxTemperature>
              </MinMaxTemperatureContainer>
            </MainWeatherGroup>

            {/* 두 번째 묶음: 상세 날씨 데이터 */}
            <WeatherDataGroup orientation={orientation}>
              {/* 풍속 */}
              <WeatherDataItem>
                <WeatherDataIconContainer>
                  <WeatherIconContainer>
                    <WeatherIcon
                      src={getWeatherThemeIcon("windspeed", themeMode)}
                      alt="풍속"
                    />
                  </WeatherIconContainer>
                  <WeatherLabel themeMode={themeMode}>풍속</WeatherLabel>
                </WeatherDataIconContainer>
                <WeatherDataValueContainer>
                  <WeatherValueNumber themeMode={themeMode}>
                    {weatherData.wind}
                  </WeatherValueNumber>
                  <WeatherValueUnit themeMode={themeMode}>m/s</WeatherValueUnit>
                </WeatherDataValueContainer>
              </WeatherDataItem>

              {/* 강수량 */}
              <WeatherDataItem>
                <WeatherDataIconContainer>
                  <WeatherIconContainer>
                    <WeatherIcon
                      src={getWeatherThemeIcon("precipitation", themeMode)}
                      alt="강수량"
                    />
                  </WeatherIconContainer>
                  <WeatherLabel themeMode={themeMode}>강수량</WeatherLabel>
                </WeatherDataIconContainer>
                <WeatherDataValueContainer>
                  <WeatherValueNumber themeMode={themeMode}>
                    {weatherData.precipitation}
                  </WeatherValueNumber>
                  <WeatherValueUnit themeMode={themeMode}>
                    mm/h
                  </WeatherValueUnit>
                </WeatherDataValueContainer>
              </WeatherDataItem>

              {/* 습도 */}
              <WeatherDataItem>
                <WeatherDataIconContainer>
                  <WeatherIconContainer>
                    <WeatherIcon
                      src={getWeatherThemeIcon("humidity", themeMode)}
                      alt="습도"
                    />
                  </WeatherIconContainer>
                  <WeatherLabel themeMode={themeMode}>습도</WeatherLabel>
                </WeatherDataIconContainer>
                <WeatherDataValueContainer>
                  <WeatherValueNumber themeMode={themeMode}>
                    {weatherData.humidity}
                  </WeatherValueNumber>
                  <WeatherValueUnit themeMode={themeMode}>%</WeatherValueUnit>
                </WeatherDataValueContainer>
              </WeatherDataItem>
            </WeatherDataGroup>
          </NewWeatherContentWrapper>
        </WeatherPanel>

        {/* 하단 메시지를 다시 독립적으로 */}
        <BottomMessage ref={messageContainerRef} themeMode={themeMode}>
          <MessageIcon themeMode={themeMode} />
          <MessageTextContainer>
            <MessageText
              ref={messageTextRef}
              isOverflowing={isTextOverflowing}
              data-text={textContent}
            >
              {textContent}
            </MessageText>
          </MessageTextContainer>
        </BottomMessage>
      </ContentWrapper>
    </MainContainer>
  );
};

export default OrganismDisplayViewLinkLotation;

// Styled Components
const MainContainer = styled.div`
  ${({ isFixedMode, displayWidth, displayHeight }) =>
    isFixedMode
      ? `
    /* 고정 모드: 정확한 픽셀 크기로 고정 */
    width: ${displayWidth}px;
    height: ${displayHeight}px;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    overflow: hidden;
  `
      : `
    /* 반응형 모드: 기존과 동일 */
    width: 100vw;
    height: 100vh;
    position: relative;
  `}

  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ backgroundImage }) => backgroundImage ? `url(${backgroundImage}) center center` : "#1f1f1f"};
  background-size: cover;
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-family: Pretendard, Arial, sans-serif;
  box-sizing: border-box;

  /* 적당한 패딩으로 복구 */
  padding: ${r(48)};

  /* 가로가 매우 길 때 좌우 패딩 증가 */
  @media (min-aspect-ratio: 21/9) {
    padding: ${r(24)} ${r(96)};
  }

  /* 세로가 길 때 상하 패딩 증가 */
  @media (max-aspect-ratio: 4/3) {
    padding: ${r(96)} ${r(24)};
  }

  /* 세로가 매우 길고 좁은 화면 (세로형 모바일) */
  @media (max-aspect-ratio: 9/16) {
    padding: ${r(120)} ${r(16)};
  }

  /* 세로가 극도로 길고 좁은 화면 */
  @media (max-aspect-ratio: 9/21) {
    padding: ${r(144)} ${r(12)};
  }

  /* 가로가 매우 길고 세로가 짧은 화면 (극단적 와이드스크린) */
  @media (min-aspect-ratio: 32/9) {
    padding: ${r(12)} ${r(120)};
  }

  /* 가로가 극도로 길고 세로가 매우 짧은 화면 */
  @media (min-aspect-ratio: 48/9) {
    padding: ${r(8)} ${r(144)};
  }

  ${({ orientation }) =>
    orientation === "portrait"
      ? `
    overflow-y: auto;
    overflow-x: hidden;
    align-items: flex-start;
  `
      : `
    overflow: hidden;
  `}
`;

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${r(20)}; /* 적당한 간격으로 복구 */
  min-height: 0; /* flex 컨테이너 shrink 허용 */

  --font-scaler: ${r(19.2)};

  /* 가로가 매우 길고 세로가 짧은 화면에서 간격 줄임 */
  @media (min-aspect-ratio: 21/9) {
    gap: ${r(15)};
  }

  @media (min-aspect-ratio: 32/9) {
    gap: ${r(12)};
  }

  @media (min-aspect-ratio: 48/9) {
    gap: ${r(8)};
  }

  /* 세로가 매우 길고 좁은 화면에서 간격 증가 */
  @media (max-aspect-ratio: 9/16) {
    gap: ${r(24)};
  }

  @media (max-aspect-ratio: 9/21) {
    gap: ${r(28)};
  }
`;

const MainPanel = styled.div`
  flex: 3.4;
  display: flex;
  flex-direction: column;
  padding: ${r(30)};
  background: linear-gradient(
    136deg,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient01", themeMode)}
      0%,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient02", themeMode)}
      50%,
    ${({ themeMode }) =>
        getThemeColor("MainPanelBackgroundLinearGradient03", themeMode)}
      100%
  );
  border-radius: ${r(20)};
  margin: ${r(10)};
  outline: 3px
    ${({ themeMode }) => getThemeColor("MainPanelOutline", themeMode)} solid; // color name = MainPanelOutline
  outline-offset: -3px;
  backdrop-filter: blur(12px);
  min-height: 0;
  overflow: visible; /* hidden -> visible로 변경 */
  flex-shrink: 1; /* shrink 허용 */

  /* 가로가 매우 길고 세로가 짧은 화면에서 패딩/마진 줄임 */
  @media (min-aspect-ratio: 21/9) {
    padding: ${r(20)};
    margin: ${r(8)};
  }

  @media (min-aspect-ratio: 32/9) {
    padding: ${r(15)};
    margin: ${r(6)};
  }

  @media (min-aspect-ratio: 48/9) {
    padding: ${r(10)};
    margin: ${r(4)};
  }

  /* 세로가 매우 길고 좁은 화면에서 패딩/마진 증가 */
  @media (max-aspect-ratio: 9/16) {
    padding: ${r(36)};
    margin: ${r(12)};
  }

  @media (max-aspect-ratio: 9/21) {
    padding: ${r(42)};
    margin: ${r(14)};
  }

  /* 1:1 비율 이후 세로가 긴 경우: WeatherPanel, BottomMessage와 동일한 radius 비율 적용 */
  @media (max-aspect-ratio: 1/1) {
    border-radius: ${rv(
      20
    )}; /* WeatherPanel, BottomMessage와 동일한 비율로 변경 */
    margin: ${rv(5)}; /* WeatherPanel, BottomMessage와 동일한 비율로 변경 */
    padding: ${rv(15)}; /* 적절한 패딩으로 조정 */
  }
`;

const HeaderContainer = styled.div`
  align-self: stretch;
  justify-content: space-between;
  align-items: flex-start;
  display: flex;
  width: 100%;
  max-width: 100%;
  flex-shrink: 0; /* 헤더가 줄어들지 않도록 */
  min-height: ${r(115.2)}; /* 최소 높이 설정 */
  margin-bottom: ${r(24)}; /* 기본 하단 마진 */

  padding: 0; /* 패딩 제거 - 센서와 정렬 맞추기 */
  flex-direction: ${({ orientation }) =>
    orientation === "portrait" ? "column" : "row"};
  ${({ orientation }) =>
    orientation === "portrait" &&
    `
    justify-content: flex-start;
    gap: ${r(19.2)};
  `}

  /* 매우 넓은 가로일 때 (1줄 레이아웃) */
  @media (min-aspect-ratio: 11/5) {
    margin-bottom: ${r(48)};
  }

  /* 짧은 가로일 때 (3줄 레이아웃) */
  @media (max-aspect-ratio: 6/5) {
    margin-bottom: ${r(24)};
  }

  /* 세로가 긴 비율일 때 (세로 스택) */
  @media (max-aspect-ratio: 4/3) {
    margin-bottom: ${r(16)};
  }

  /* 1:1 비율 이후 세로가 긴 경우: 타이틀과 시간 영역 사이 공간 50% 줄이기 */
  @media (max-aspect-ratio: 1/1) {
    ${({ orientation }) =>
      orientation === "portrait" &&
      `
      gap: ${rv(5)}; /* 기존 19.2의 50% 줄임 */
    `}
  }
`;

const TitleSection = styled.div`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${r(9.6)};
  display: flex;
  padding: ${r(38.4)} ${r(38.4)} 0 ${r(38.4)};

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    gap: ${(9.6 / 1920) * 100}vw;
    padding: ${(25 / 1920) * 100}vw ${(38.4 / 1920) * 100}vw 0
      ${(38.4 / 1920) * 100}vw; /* 위쪽 패딩 줄임 */
  }

  /* 매우 넓은 비율 (3.5:1 이상)에서 더 위로 */
  @media (min-aspect-ratio: 3.5/1) {
    padding: ${(15 / 1920) * 100}vw ${(38.4 / 1920) * 100}vw 0
      ${(38.4 / 1920) * 100}vw; /* 더 위로 올림 */
  }

  /* 매우 넓은 비율 (4:1 이상)에서 조금 더 위로 */
  @media (min-aspect-ratio: 4/1) {
    padding: ${(10 / 1920) * 100}vw ${(38.4 / 1920) * 100}vw 0
      ${(38.4 / 1920) * 100}vw; /* 가장 위로 */
  }

  /* 1:1 비율 이후 세로가 긴 경우만 VH 기준으로 */
  @media (max-aspect-ratio: 1/1) {
    padding: ${rv(20)} ${rv(20)} 0 ${rv(20)};
    gap: ${rv(5)};
  }
`;

const MainTitle = styled.h1`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2.5 * var(--font-scaler));
  font-family: Pretendard;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(57.6 / 1920) * 100}vw;
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }
`;

const DateTimeSection = styled.div`
  flex-direction: column;
  justify-content: flex-start;
  align-items: ${({ orientation }) =>
    orientation === "portrait" ? "flex-start" : "flex-end"};
  gap: ${r(3.84)};
  display: flex;

  ${({ orientation }) =>
    orientation === "portrait"
      ? `
        padding: 0 0 0 ${r(38.4)};
        margin-top: ${r(-45)};
      `
      : `
        padding: ${r(38.4)} 0 0 ${r(38.4)};
  `}

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    gap: ${(3.84 / 1920) * 100}vw;
    ${({ orientation }) =>
      orientation === "portrait"
        ? `
          padding: 0 0 0 ${(38.4 / 1920) * 100}vw;
          margin-top: ${(-45 / 1920) * 100}vw;
        `
        : `
          padding: ${(25 / 1920) * 100}vw 0 0 ${
            (38.4 / 1920) * 100
          }vw; /* 38.4에서 25로 줄여서 위로 올림 */
    `}
  }

  /* 매우 넓은 비율 (3.5:1 이상)에서 더 위로 */
  @media (min-aspect-ratio: 3.5/1) {
    ${({ orientation }) =>
      orientation === "landscape"
        ? `
          padding: ${(15 / 1920) * 100}vw 0 0 ${
            (38.4 / 1920) * 100
          }vw; /* 더 위로 올림 */
        `
        : ``}
  }

  /* 매우 넓은 비율 (4:1 이상)에서 조금 더 위로 */
  @media (min-aspect-ratio: 4/1) {
    ${({ orientation }) =>
      orientation === "landscape"
        ? `
          padding: ${(10 / 1920) * 100}vw 0 0 ${
            (38.4 / 1920) * 100
          }vw; /* 가장 위로 */
        `
        : ``}
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    gap: ${rv(2)};
    ${({ orientation }) =>
      orientation === "portrait"
        ? `
          padding: 0 0 0 ${rv(20)};
          margin-top: ${rv(-12)};
        `
        : `
          padding: ${rv(20)} 0 0 ${rv(20)};
    `}
  }
`;

const DateContainer = styled.div`
  flex-direction: row; /* column → row로 변경 */
  justify-content: space-between; /* center → space-between으로 변경 */
  align-items: center; /* flex-end → center로 변경 */
  display: flex;
  gap: 0; /* 간격 완전 제거 */
  width: 100%; /* 전체 너비 사용 */
`;

const DateRow = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${r(13.44)};
  display: flex;
`;

const DateText = styled.span`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2.05 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 400;
  line-height: 1.2;

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(34.56 / 1920) * 100}vw;
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }
`;

const TimeText = styled.div`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2.05 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 400;
  line-height: 1.2;

  width: ${r(200)};
  text-align: center;
  flex-shrink: 0;

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(34.56 / 1920) * 100}vw;
    width: ${(200 / 1920) * 100}vw;
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
    width: ${rv(100)};
  }
`;

const SensorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${r(16)};
  grid-template-rows: auto;
  place-items: stretch;
  width: 100%;
  height: auto;
  flex: 1;
  padding: 0;
  justify-content: center;
  align-items: center;
  min-height: 0;
  flex-shrink: 1;

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    gap: ${(16 / 1920) * 100}vw;
  }

  /* 기본 1행 6열 배치 - 라인 없이 */
  > *:nth-child(1) {
    grid-column: 1;
    grid-row: 1;
  } /* 온도 */
  > *:nth-child(2) {
    display: none;
  } /* 라인1 제거 */
  > *:nth-child(3) {
    grid-column: 2;
    grid-row: 1;
  } /* 오존 */
  > *:nth-child(4) {
    display: none;
  } /* 라인2 제거 */
  > *:nth-child(5) {
    grid-column: 3;
    grid-row: 1;
  } /* 습도 */
  > *:nth-child(6) {
    display: none;
  } /* 라인3 제거 */
  > *:nth-child(7) {
    grid-column: 4;
    grid-row: 1;
  } /* 미세먼지 */
  > *:nth-child(8) {
    display: none;
  } /* 라인4 제거 */
  > *:nth-child(9) {
    grid-column: 5;
    grid-row: 1;
  } /* VOC */
  > *:nth-child(10) {
    display: none;
  } /* 라인5 제거 */
  > *:nth-child(11) {
    grid-column: 6;
    grid-row: 1;
  } /* 이산화탄소 */

  /* 가로가 줄어들어서 1줄이 힘들 때만 2줄로 (1.2:1 이하) */
  @media (max-aspect-ratio: 6/5) {
    grid-template-columns: repeat(3, 1fr); /* 라인 제거: 3개씩 */

    grid-template-rows: repeat(2, auto); /* 1fr -> auto로 변경 */
    gap: ${r(14)}; /* 30 → 14로 줄임 */
    justify-content: center; /* 중앙 정렬 추가 */
    align-items: center; /* 중앙 정렬 추가 */

    /* 2행 3열 배치 - 라인 없이 */
    > *:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    } /* 온도 */
    > *:nth-child(2) {
      display: none;
    } /* 라인1 제거 */
    > *:nth-child(3) {
      grid-column: 2;
      grid-row: 1;
    } /* 오존 */
    > *:nth-child(4) {
      display: none;
    } /* 라인2 제거 */
    > *:nth-child(5) {
      grid-column: 3;
      grid-row: 1;
    } /* 습도 */
    > *:nth-child(6) {
      display: none;
    } /* 라인3 제거 */
    > *:nth-child(7) {
      grid-column: 1;
      grid-row: 2;
    } /* 미세먼지 */
    > *:nth-child(8) {
      display: none;
    } /* 라인4 제거 */
    > *:nth-child(9) {
      grid-column: 2;
      grid-row: 2;
    } /* VOC */
    > *:nth-child(10) {
      display: none;
    } /* 라인5 제거 */
    > *:nth-child(11) {
      grid-column: 3;
      grid-row: 2;
    } /* 이산화탄소 */

    /* 2행 3열 배치 - 라인 없이 */
    > *:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    } /* 온도 */
    > *:nth-child(2) {
      display: none;
    } /* 라인1 제거 */
    > *:nth-child(3) {
      grid-column: 2;
      grid-row: 1;
    } /* 오존 */
    > *:nth-child(4) {
      display: none;
    } /* 라인2 제거 */
    > *:nth-child(5) {
      grid-column: 3;
      grid-row: 1;
    } /* 습도 */
    > *:nth-child(6) {
      display: none;
    } /* 라인3 제거 */
    > *:nth-child(7) {
      grid-column: 1;
      grid-row: 2;
    } /* 미세먼지 */
    > *:nth-child(8) {
      display: none;
    } /* 라인4 제거 */
    > *:nth-child(9) {
      grid-column: 2;
      grid-row: 2;
    } /* VOC */
    > *:nth-child(10) {
      display: none;
    } /* 라인5 제거 */
    > *:nth-child(11) {
      grid-column: 3;
      grid-row: 2;
    } /* 이산화탄소 */
  }

  /* 가로가 더 줄어들어서 2줄도 힘들 때만 3줄로 (0.75:1 이하) */
  @media (max-aspect-ratio: 0.75) {
    grid-template-columns: 1fr 1fr !important; /* 완전히 균등 분할 */
    grid-template-rows: repeat(3, auto) !important; /* 1fr -> auto로 변경 */
    gap: ${r(8)} !important; /* 12 → 8로 더 줄임 */
    justify-content: center !important; /* 중앙 정렬 추가 */
    align-items: center !important; /* 중앙 정렬 추가 */
    justify-items: center !important; /* 각 그리드 아이템 중앙 정렬 */
    width: 100% !important; /* 전체 너비 사용 */
    margin: 0 auto !important; /* 전체 그리드 중앙 정렬 */
    place-items: center !important; /* 모든 아이템 중앙 정렬 */

    /* 매우 세로가 긴 비율 (0.35:1 이하) - 90% 크기 */
    @media (max-aspect-ratio: 0.35) {
      gap: ${r(7.2)} !important; /* 8 * 0.9 = 7.2 */
    }

    /* 3행 2열 배치 - 라인 없이 */
    > *:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    } /* 온도 */
    > *:nth-child(2) {
      display: none;
    } /* 라인1 제거 */
    > *:nth-child(3) {
      grid-column: 2;
      grid-row: 1;
    } /* 오존 */
    > *:nth-child(4) {
      display: none;
    } /* 라인2 제거 */
    > *:nth-child(5) {
      grid-column: 1;
      grid-row: 2;
    } /* 습도 */
    > *:nth-child(6) {
      display: none;
    } /* 라인3 제거 */
    > *:nth-child(7) {
      grid-column: 2;
      grid-row: 2;
    } /* 미세먼지 */
    > *:nth-child(8) {
      display: none;
    } /* 라인4 제거 */
    > *:nth-child(9) {
      grid-column: 1;
      grid-row: 3;
    } /* VOC */
    > *:nth-child(10) {
      display: none;
    } /* 라인5 제거 */
    > *:nth-child(11) {
      grid-column: 2;
      grid-row: 3;
    } /* 이산화탄소 */
  }

  /* 진짜 세로가 긴 비율 (0.5:1 이하): 6줄 1개씩 */
  @media (max-aspect-ratio: 1/2) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;

    gap: ${r(10)}; /* 20 → 10으로 줄임 */
    justify-content: center; /* 중앙 정렬 추가 */
    align-items: center; /* 중앙 정렬 추가 */

    /* 라인들 모두 제거 */
    > *:nth-child(even) {
      display: none;
    }

    /* 그리드 위치 초기화 */
    > * {
      grid-column: unset;
      grid-row: unset;
    }
  }
`;

const SensorItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${r(4)};
  width: 100%;
  height: auto;
  padding: ${r(4)};
  white-space: nowrap;
  text-align: center;
  box-sizing: border-box;
  min-height: 0;

  > * {
    align-self: center;
    text-align: center;
  }

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    gap: ${(4 / 1920) * 100}vw;
    padding: ${(4 / 1920) * 100}vw;
  }
`;

const WeatherPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    136deg,
    ${({ themeMode }) =>
        getThemeColor("WeatherPanelBackgroundLinearGradient01", themeMode)}
      0%,
    ${({ themeMode }) =>
        getThemeColor("WeatherPanelBackgroundLinearGradient02", themeMode)}
      50%,
    ${({ themeMode }) =>
        getThemeColor("WeatherPanelBackgroundLinearGradient03", themeMode)}
      100%
  );
  border-radius: ${r(20)};
  margin: ${r(10)};
  outline: 3px
    ${({ themeMode }) => getThemeColor("WeatherPanelOutline", themeMode)} solid; // color name = WeatherPanelOutline
  outline-offset: -3px;
  backdrop-filter: blur(12px);
  /* 기본 패딩을 가로 모드 기준으로 더 증가 */
  padding: ${r(60)}; /* 40 → 60으로 패딩 대폭 증가 */

  /* 2017:917 이상에서만 가로 기준 패딩 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    padding: ${r(60)} ${(120 / 1920) * 100}vw; /* 좌우 패딩만 가로 기준으로 증가 */
  }
  min-height: 0;
  overflow: hidden;

  flex-shrink: 1; /* shrink 허용 */

  /* 가로가 매우 길고 세로가 짧은 화면에서 패딩 더 증가 */
  @media (min-aspect-ratio: 21/9) {
    padding: ${r(50)}; /* 32 → 50으로 패딩 증가 */
    margin: ${r(8)};
  }

  @media (min-aspect-ratio: 21/9) and (min-aspect-ratio: 2.2/1) {
    padding: ${r(50)} ${(100 / 1920) * 100}vw; /* 좌우 패딩만 가로 기준으로 증가 */
  }

  @media (min-aspect-ratio: 32/9) {
    padding: ${r(45)}; /* 28 → 45로 패딩 증가 */
    margin: ${r(6)};
  }

  @media (min-aspect-ratio: 32/9) and (min-aspect-ratio: 2.2/1) {
    padding: ${r(45)} ${(90 / 1920) * 100}vw; /* 좌우 패딩만 가로 기준으로 증가 */
  }

  @media (min-aspect-ratio: 48/9) {
    padding: ${r(40)}; /* 24 → 40으로 패딩 증가 */
    margin: ${r(4)};
  }

  @media (min-aspect-ratio: 48/9) and (min-aspect-ratio: 2.2/1) {
    padding: ${r(40)} ${(80 / 1920) * 100}vw; /* 좌우 패딩만 가로 기준으로 증가 */
  }

  /* 세로가 매우 길고 좁은 화면에서 패딩 조정 - 세로 모드이므로 패딩 줄임 */
  @media (max-aspect-ratio: 9/16) {
    padding: ${r(30)}; /* 45 → 30으로 패딩 감소 (세로 모드) */
    margin: ${r(12)};
  }

  @media (max-aspect-ratio: 9/21) {
    padding: ${r(25)}; /* 50 → 25로 패딩 감소 (세로 모드) */
    margin: ${r(14)};
  }

  /* 1:1 비율 이후 세로가 긴 경우: 세로에서 너무 커지지 않게 VH 값 작게 */
  @media (max-aspect-ratio: 1/1) {
    border-radius: ${rv(20)}; /* MainPanel과 동일한 비율로 변경 */
    margin: ${rv(5)}; /* VH 기준으로 작게 */
    padding: ${rv(15)}; /* 20 → 15로 패딩 감소 (세로 모드) */
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 크기 보장 */
  @media (max-height: 900px) {
    border-radius: max(${r(20)}, 12px);
    margin: max(${r(10)}, 6px);
    padding: max(${r(25)}, 15px);
  }

  @media (max-height: 700px) {
    border-radius: max(${r(20)}, 10px);
    margin: max(${r(10)}, 5px);
    padding: max(${r(25)}, 12px);
  }

  @media (max-height: 500px) {
    border-radius: max(${r(20)}, 8px);
    margin: max(${r(10)}, 4px);
    padding: max(${r(25)}, 10px);
  }
`;

const WeatherIcon = styled.img`
  width: ${r(64)};
  height: ${r(64)};

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    width: ${(64 / 1920) * 100}vw;
    height: ${(64 / 1920) * 100}vw;
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    width: ${rv(32)};
    height: ${rv(32)};
  }
`;

const WeatherIconContainer = styled.div`
  width: ${r(64)};
  height: ${r(64)};
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    width: ${(55 / 1920) * 100}vw; /* 70에서 55로 완만하게 줄임 */
    height: ${(55 / 1920) * 100}vw;
  }

  /* 아이콘이 컨테이너에 딱 맞게 조정 */
  > * {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain;
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    width: ${rv(32)};
    height: ${rv(32)};
  }
`;

const WeatherLabel = styled.div`
  color: ${({ themeMode }) =>
    getThemeColor(
      "WeatherLabelColor",
      themeMode
    )}; // color name = WeatherLabelColor
  font-size: calc(1.6 * var(--font-scaler));
  font-family: Pretendard;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(28 / 1920) * 100}vw; /* 35에서 28로 완만하게 줄임 */
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }
`;

const WeatherValueNumber = styled.span`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2.5 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 400;
  line-height: 1.2;

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(42 / 1920) * 100}vw; /* 55에서 42로 완만하게 줄임 */
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }
`;

const WeatherValueUnit = styled.span`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(1.6 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 500;
  line-height: 1.2;

  /* 2.2:1 비율 이상에서만 가로 기준 스케일링 적용 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(28 / 1920) * 100}vw; /* 35에서 28로 완만하게 줄임 */
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }
`;

const BottomMessage = styled.div`
  padding: ${r(15)};
  background: ${({ themeMode }) =>
    getThemeColor(
      "bottomMessageBackground",
      themeMode
    )}; // color name = bottomMessageBackground
  border-radius: ${r(20)}; /* MainPanel, WeatherPanel과 동일한 radius */
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  gap: ${r(15)};
  flex-shrink: 0; /* 크기가 줄어들지 않도록 */

  margin: ${r(
    10
  )}; /* MainPanel, WeatherPanel과 동일한 margin으로 좌우 길이 맞춤 */

  /* 1:1 비율 이후 세로가 긴 경우: 세로에서 너무 커지지 않게 VH 값 작게 */
  @media (max-aspect-ratio: 1/1) {
    padding: ${rv(8)}; /* VH 기준으로 작게 */
    border-radius: ${rv(20)}; /* MainPanel, WeatherPanel과 동일한 비율로 변경 */
    gap: ${rv(8)}; /* VH 기준으로 작게 */
    margin: ${rv(5)}; /* VH 기준으로 작게 */
  }
`;

const MessageIcon = styled.div`
  width: ${r(28.8)};
  height: ${r(28.8)};
  background: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  border-radius: 50%;

  /* 1:1 비율 이후 세로가 긴 경우: 세로에서 너무 커지지 않게 VH 값 작게 */
  @media (max-aspect-ratio: 1/1) {
    width: ${rv(15)}; /* VH 기준으로 작게 */
    height: ${rv(15)};
  }
`;

const MessageTextContainer = styled.div`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
`;

const MessageText = styled.div`
  font-size: calc(1.8 * var(--font-scaler));
  font-weight: 600;
  white-space: nowrap;

  /* 1:1 비율 이후 세로가 긴 경우: 세로에서 너무 커지지 않게 VH 값 작게 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)}; /* VH 기준으로 작게 */
  }

  ${({ isOverflowing }) =>
    isOverflowing
      ? css`
          display: inline-block;
          will-change: transform;
          animation: ${marqueeAnimation} 15s linear infinite;
          position: relative;

          &::after {
            content: attr(data-text);
            position: absolute;
            left: 100%;
            top: 0;
            padding-left: 4em; /* Gap between repeated text */
            white-space: nowrap;
          }
        `
      : css`
          overflow: visible;
        `}
`;

const StyledLine653 = styled.div`
  width: ${r(5)};
  height: ${r(117)}; /* 센서 높이의 33% */
  opacity: 0.6;
  background: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  align-self: center;

  /* 매우 넓은 가로 비율 (3:1 이상) */
  @media (min-aspect-ratio: 3/1) {
    width: ${r(3)};
    height: ${r(70)};
  }

  /* 넓은 가로 비율 (21:9 이상) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    width: ${r(4)};
    height: ${r(80)};
  }

  /* 가로가 줄어들면서 점진적으로 작아짐 (2.0:1 ~ 1.2:1) */
  @media (max-aspect-ratio: 2/1) and (min-aspect-ratio: 6/5) {
    width: ${r(4.8)};
    height: ${r(113)};
  }

  /* 2줄 레이아웃 (1.2:1 ~ 0.75:1 초과) */
  @media (max-aspect-ratio: 6/5) and (min-aspect-ratio: 0.751) {
    width: ${r(4.7)}; /* 4.5 → 4.7 (40% 완만) */
    height: ${r(113)}; /* 107 → 113 (40% 완만) */
  }

  /* 3줄 레이아웃 (0.75:1 이하) */
  @media (max-aspect-ratio: 0.75) {
    width: ${r(4)} !important; /* 4.44 → 4로 더 작게 */
    height: ${r(90)} !important; /* 107 → 90으로 더 작게 */
  }

  /* 매우 세로가 긴 비율 (0.35:1 이하) - 90% 크기 */
  @media (max-aspect-ratio: 0.35) {
    width: ${r(3.6)} !important; /* 4 * 0.9 = 3.6 */
    height: ${r(81)} !important; /* 90 * 0.9 = 81 */
  }

  /* 6줄 레이아웃 (0.5:1 이하) */
  @media (max-aspect-ratio: 1/2) {
    width: ${r(5)}; /* 3.6 → 5 */
    height: ${r(100)}; /* 80 → 100 */
  }

  /* 1:1 비율 이후 세로가 긴 경우: 적절한 크기 유지하면서 VH 기준 스케일링 */
  @media (max-aspect-ratio: 1/1) {
    /* 세로가 긴 비율에서도 잘 보이는 구분선 */
    width: ${rv(6)}; /* 적절한 두께 */
    height: ${rv(130)}; /* 적절한 높이 */
  }
`;

const SensorSeparator = ({ themeMode }) => {
  return <StyledLine653 themeMode={themeMode} />;
};

// AtomsGauge를 SensorItem 크기에 맞게 조절하는 래퍼
const GaugeWrapper = styled.div`
  width: 80%;
  height: 80%;
  max-width: ${r(104)};
  max-height: ${r(104)};
  display: flex;
  align-items: center;
  justify-content: center;

  /* AtomsGauge 컴포넌트 전체 크기 조절 */
  > * {
    transform: scale(0.7);
    transform-origin: center center;
  }

  /* 2017:917 이상에서 크기 증가 - transform scale로만 조절 */
  @media (min-aspect-ratio: 2.2/1) {
    > * {
      transform: scale(0.9); /* 기본 0.7에서 0.9로 증가 */
    }
  }

  @media (min-aspect-ratio: 2.5/1) {
    > * {
      transform: scale(1.1); /* 더 크게 */
    }
  }

  @media (min-aspect-ratio: 3/1) {
    > * {
      transform: scale(1.3); /* 가장 크게 */
    }
  }

  /* 기존 비율별 조정은 유지하되 VH 단위 제거 */
  /* 가장 넓은 가로 비율 (3:1 이상) - 가장 작게 */
  @media (min-aspect-ratio: 3/1) {
    width: 60%;
    height: 60%;
    max-width: ${r(78)};
    max-height: ${r(78)};
    > * {
      transform: scale(1.3); /* VH 단위 제거하고 transform만 사용 */
    }
  }

  /* 넓은 가로 비율 (21:9 ~ 3:1) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    width: 70%;
    height: 70%;
    max-width: ${r(87)};
    max-height: ${r(87)};
    > * {
      transform: scale(1.1);
    }
  }

  /* 중간 넓은 가로 비율 (2.2:1 ~ 21:9) */
  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) {
    width: 75%;
    height: 75%;
    max-width: ${r(115)};
    max-height: ${r(115)};
    > * {
      transform: scale(0.9);
    }
  }

  /* 약간 넓은 가로 비율 (2.0:1 ~ 2.2:1) - 가장 크게 */
  @media (min-aspect-ratio: 2/1) and (max-aspect-ratio: 2.2) {
    width: 80%;
    height: 80%;
    max-width: ${r(135)};
    max-height: ${r(135)};
    > * {
      transform: scale(0.85);
    }
  }

  /* 가로가 줄어들면서 점진적으로 커짐 (1.2:1 ~ 2.0:1) */
  @media (max-aspect-ratio: 2/1) and (min-aspect-ratio: 6/5) {
    width: 75%;
    height: 75%;
    max-width: ${r(145)};
    max-height: ${r(145)};
    > * {
      transform: scale(0.8);
    }
  }

  /* 2줄 레이아웃 (1.2:1 ~ 0.75:1 초과) */
  @media (max-aspect-ratio: 6/5) and (min-aspect-ratio: 0.751) {
    width: 60%;
    height: 60%;
    max-width: ${r(97)};
    max-height: ${r(97)};
    > * {
      transform: scale(0.6);
    }
  }

  /* 3줄 레이아웃 (0.75:1 이하) */
  @media (max-aspect-ratio: 0.75) {
    width: 55% !important;
    height: 55% !important;
    max-width: ${r(115)} !important;
    max-height: ${r(115)} !important;
    > * {
      transform: scale(0.55) !important;
    }
  }

  /* 매우 세로가 긴 비율 (0.35:1 이하) - 90% 크기 */
  @media (max-aspect-ratio: 0.35) {
    width: 49.5% !important;
    height: 49.5% !important;
    max-width: ${r(104)} !important;
    max-height: ${r(104)} !important;
    > * {
      transform: scale(0.495) !important;
    }
  }

  /* 6줄 레이아웃 (0.5:1 이하) */
  @media (max-aspect-ratio: 1/2) {
    width: 60%;
    height: 60%;
    max-width: ${r(125)};
    max-height: ${r(125)};
    > * {
      transform: scale(0.6);
    }
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    width: 70%;
    height: 70%;
    max-width: ${rv(50)};
    max-height: ${rv(50)};
    > * {
      transform: scale(0.65);
    }
  }

  /* 2:3 비율 이후 더 세로가 긴 경우 */
  @media (max-aspect-ratio: 2/3) {
    width: 60%;
    height: 60%;
    max-width: ${rv(48)};
    max-height: ${rv(48)};
    > * {
      transform: scale(0.55);
    }
  }
`;

// 새로운 날씨 컨텐츠 래퍼 - 가로/세로 모드에 따라 다른 배치
const NewWeatherContentWrapper = styled.div`
  display: flex;
  flex-direction: row; /* 기본: 가로 배치 */
  flex: 1;

  /* 가로 모드: 모든 요소를 균등 분배 */
  ${({ orientation }) =>
    orientation === "landscape"
      ? `
    gap: 0;
    justify-content: space-between;
  `
      : `
    /* 세로 모드: 중앙 배치 */
    gap: ${r(30)};
    justify-content: center;
  `}

  align-items: center;
  width: 100%;
  min-height: 0;
  flex-shrink: 1;

  /* 세로가 매우 길어지면 세로 배치 (9:21 = 0.428:1) */
  @media (max-aspect-ratio: 9/21) {
    flex-direction: column;
    gap: ${r(20)};
  }

  /* 높이 기반 반응형 */
  @media (max-height: 900px) {
    gap: max(${r(30)}, 18px);
  }

  @media (max-height: 700px) {
    gap: max(${r(30)}, 15px);
  }

  @media (max-height: 500px) {
    gap: max(${r(30)}, 12px);
  }
`;

// 첫 번째 묶음: 메인 날씨 정보
const MainWeatherGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${r(30)};
  justify-content: flex-start; /* center → flex-start로 변경하여 왼쪽 끝단 정렬 */

  /* 2017:917 이상에서만 가로 기준 스케일링 적용 - 매우 완만하게 */
  @media (min-aspect-ratio: 2.2/1) {
    gap: ${(20 / 1920) * 100}vw; /* 35에서 20으로 훨씬 완만하게 */
  }

  /* 가장 넓은 가로 비율 (3:1 이상) - 가장 작게 */
  @media (min-aspect-ratio: 3/1) {
    gap: ${r(20)};
  }

  @media (min-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    gap: ${(15 / 1920) * 100}vw; /* 25에서 15로 훨씬 완만하게 */
  }

  /* 넓은 가로 비율 (21:9 ~ 3:1) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    gap: ${r(25)};
  }

  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    gap: ${(18 / 1920) * 100}vw; /* 30에서 18로 훨씬 완만하게 */
  }

  /* 중간 넓은 가로 비율 (2.2:1 ~ 21:9) */
  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) {
    gap: ${r(30)};
  }

  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) and (min-aspect-ratio: 2.2/1) {
    gap: ${(22 / 1920) * 100}vw; /* 35에서 22로 훨씬 완만하게 */
  }

  /* 약간 넓은 가로 비율 (2.0:1 ~ 2.2:1) - 가장 크게 */
  @media (min-aspect-ratio: 2/1) and (max-aspect-ratio: 2.2) {
    gap: ${r(35)};
  }

  /* 세로가 매우 길어지면 간격 증가 */
  @media (max-aspect-ratio: 9/21) {
    gap: ${r(35)};
  }

  /* 높이 기반 반응형 */
  @media (max-height: 900px) {
    gap: max(${r(30)}, 18px);
  }

  @media (max-height: 700px) {
    gap: max(${r(30)}, 15px);
  }

  @media (max-height: 500px) {
    gap: max(${r(30)}, 12px);
  }
`;

// 두 번째 묶음: 상세 날씨 데이터 - 가로/세로 모드에 따라 다른 표시
const WeatherDataGroup = styled.div`
  /* 가로 모드: 개별 요소로 분리, 세로 모드: 그룹으로 유지 */
  ${({ orientation }) =>
    orientation === "landscape"
      ? `
    display: contents; /* 개별 요소로 분리 */
  `
      : `
    /* 세로 모드: 기존 그룹 형태 유지 */
    display: flex;
    flex-direction: row;
    gap: ${r(25)};
    justify-content: center;
    align-items: center;
    
    /* 매우 좁은 세로 비율에서 세로 배치 (456:917 ~ 394:917) */
    @media (max-aspect-ratio: 456/917) and (min-aspect-ratio: 394/917) {
      flex-direction: column;
      gap: ${r(15)};
    }
  `}
`;

// 큰 날씨 아이콘
const MainWeatherIcon = styled.img`
  width: ${r(140)};
  height: ${r(140)};

  /* 2017:917 이상에서만 가로 기준 스케일링 적용 - 매우 완만하게 */
  @media (min-aspect-ratio: 2.2/1) {
    width: ${(150 / 1920) * 100}vw; /* 160에서 150으로 완만하게 */
    height: ${(150 / 1920) * 100}vw;
  }

  /* 가장 넓은 가로 비율 (3:1 이상) - 가장 작게 */
  @media (min-aspect-ratio: 3/1) {
    width: ${r(100)};
    height: ${r(100)};
  }

  @media (min-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    width: ${(110 / 1920) * 100}vw; /* 120에서 110으로 완만하게 */
    height: ${(110 / 1920) * 100}vw;
  }

  /* 넓은 가로 비율 (21:9 ~ 3:1) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    width: ${r(120)};
    height: ${r(120)};
  }

  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    width: ${(130 / 1920) * 100}vw; /* 140에서 130으로 완만하게 */
    height: ${(130 / 1920) * 100}vw;
  }

  /* 중간 넓은 가로 비율 (2.2:1 ~ 21:9) */
  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) {
    width: ${r(140)};
    height: ${r(140)};
  }

  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) and (min-aspect-ratio: 2.2/1) {
    width: ${(145 / 1920) * 100}vw; /* 160에서 145로 완만하게 */
    height: ${(145 / 1920) * 100}vw;
  }

  /* 약간 넓은 가로 비율 (2.0:1 ~ 2.2:1) - 가장 크게 */
  @media (min-aspect-ratio: 2/1) and (max-aspect-ratio: 2.2) {
    width: ${r(160)};
    height: ${r(160)};
  }

  /* 세로가 매우 길고 좁은 화면 */
  @media (max-aspect-ratio: 9/16) {
    width: ${r(160)};
    height: ${r(160)};
  }

  @media (max-aspect-ratio: 9/21) {
    width: ${r(180)};
    height: ${r(180)};
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    width: ${rv(70)};
    height: ${rv(70)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 크기 보장 */
  @media (max-height: 900px) {
    width: max(${r(140)}, 84px);
    height: max(${r(140)}, 84px);
  }

  @media (max-height: 700px) {
    width: max(${r(140)}, 70px);
    height: max(${r(140)}, 70px);
  }

  @media (max-height: 500px) {
    width: max(${r(140)}, 56px);
    height: max(${r(140)}, 56px);
  }
`;

// 날씨 정보 컨테이너
const WeatherInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${r(8)};

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    gap: ${rv(4)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 간격 보장 */
  @media (max-height: 900px) {
    gap: max(${r(8)}, 5px);
  }

  @media (max-height: 700px) {
    gap: max(${r(8)}, 4px);
  }

  @media (max-height: 500px) {
    gap: max(${r(8)}, 3px);
  }
`;

// 날씨 상태 텍스트
const WeatherStatus = styled.div`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(1.8 * var(--font-scaler));
  font-family: Pretendard;
  font-weight: 500;

  /* 2017:917 이상에서만 가로 기준 스케일링 적용 - 완만하게 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(35 / 1920) * 100}vw; /* 완만한 증가 */
  }

  /* 가장 넓은 가로 비율 (3:1 이상) - 가장 작게 */
  @media (min-aspect-ratio: 3/1) {
    font-size: calc(1.4 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(28 / 1920) * 100}vw; /* 완만한 증가 */
  }

  /* 넓은 가로 비율 (21:9 ~ 3:1) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    font-size: calc(1.6 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(32 / 1920) * 100}vw; /* 완만한 증가 */
  }

  /* 중간 넓은 가로 비율 (2.2:1 ~ 21:9) */
  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) {
    font-size: calc(1.8 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(35 / 1920) * 100}vw; /* 완만한 증가 */
  }

  /* 약간 넓은 가로 비율 (2.0:1 ~ 2.2:1) - 가장 크게 */
  @media (min-aspect-ratio: 2/1) and (max-aspect-ratio: 2.2) {
    font-size: calc(2 * var(--font-scaler));
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 폰트 크기 보장 */
  @media (max-height: 900px) {
    font-size: max(calc(1.8 * var(--font-scaler)), 18px);
  }

  @media (max-height: 700px) {
    font-size: max(calc(1.8 * var(--font-scaler)), 16px);
  }

  @media (max-height: 500px) {
    font-size: max(calc(1.8 * var(--font-scaler)), 14px);
  }
`;

// 메인 온도 컨테이너
const MainTemperatureContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${r(4)};

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    gap: ${rv(2)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 간격 보장 */
  @media (max-height: 900px) {
    gap: max(${r(4)}, 2px);
  }

  @media (max-height: 700px) {
    gap: max(${r(4)}, 2px);
  }

  @media (max-height: 500px) {
    gap: max(${r(4)}, 1px);
  }
`;

// 메인 온도 숫자
const MainTemperatureNumber = styled.span`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(3.5 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 400;
  line-height: 1;

  /* 2017:917 이상에서만 가로 기준 스케일링 적용 - 매우 완만하게 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(68 / 1920) * 100}vw; /* 80에서 68로 완만하게 */
  }

  /* 가장 넓은 가로 비율 (3:1 이상) - 가장 작게 */
  @media (min-aspect-ratio: 3/1) {
    font-size: calc(2.8 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(55 / 1920) * 100}vw; /* 65에서 55로 완만하게 */
  }

  /* 넓은 가로 비율 (21:9 ~ 3:1) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    font-size: calc(3.1 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(60 / 1920) * 100}vw; /* 72에서 60으로 완만하게 */
  }

  /* 중간 넓은 가로 비율 (2.2:1 ~ 21:9) */
  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) {
    font-size: calc(3.5 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(65 / 1920) * 100}vw; /* 80에서 65로 완만하게 */
  }

  /* 약간 넓은 가로 비율 (2.0:1 ~ 2.2:1) - 가장 크게 */
  @media (min-aspect-ratio: 2/1) and (max-aspect-ratio: 2.2) {
    font-size: calc(3.8 * var(--font-scaler));
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 폰트 크기 보장 */
  @media (max-height: 900px) {
    font-size: max(calc(3.5 * var(--font-scaler)), 35px);
  }

  @media (max-height: 700px) {
    font-size: max(calc(3.5 * var(--font-scaler)), 28px);
  }

  @media (max-height: 500px) {
    font-size: max(calc(3.5 * var(--font-scaler)), 24px);
  }
`;

// 메인 온도 단위
const MainTemperatureUnit = styled.span`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(2 * var(--font-scaler));
  font-family: Poppins;
  font-weight: 500;
  line-height: 1;

  /* 2017:917 이상에서만 가로 기준 스케일링 적용 - 매우 완만하게 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(38 / 1920) * 100}vw; /* 45에서 38로 완만하게 */
  }

  /* 가장 넓은 가로 비율 (3:1 이상) - 가장 작게 */
  @media (min-aspect-ratio: 3/1) {
    font-size: calc(1.6 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(30 / 1920) * 100}vw; /* 35에서 30으로 완만하게 */
  }

  /* 넓은 가로 비율 (21:9 ~ 3:1) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    font-size: calc(1.8 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(33 / 1920) * 100}vw; /* 40에서 33으로 완만하게 */
  }

  /* 중간 넓은 가로 비율 (2.2:1 ~ 21:9) */
  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) {
    font-size: calc(2 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(36 / 1920) * 100}vw; /* 45에서 36으로 완만하게 */
  }

  /* 약간 넓은 가로 비율 (2.0:1 ~ 2.2:1) - 가장 크게 */
  @media (min-aspect-ratio: 2/1) and (max-aspect-ratio: 2.2) {
    font-size: calc(2.2 * var(--font-scaler));
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 폰트 크기 보장 */
  @media (max-height: 900px) {
    font-size: max(calc(2 * var(--font-scaler)), 20px);
  }

  @media (max-height: 700px) {
    font-size: max(calc(2 * var(--font-scaler)), 16px);
  }

  @media (max-height: 500px) {
    font-size: max(calc(2 * var(--font-scaler)), 14px);
  }
`;

// 최저/최고 온도 컨테이너
const MinMaxTemperatureContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${r(4)};

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    gap: ${rv(2)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 간격 보장 */
  @media (max-height: 900px) {
    gap: max(${r(4)}, 2px);
  }

  @media (max-height: 700px) {
    gap: max(${r(4)}, 2px);
  }

  @media (max-height: 500px) {
    gap: max(${r(4)}, 1px);
  }
`;

// 최저/최고 온도 텍스트
const MinMaxTemperature = styled.div`
  color: ${({ themeMode }) =>
    getThemeColor("mainColor", themeMode)}; // color name = mainColor
  font-size: calc(1.8 * var(--font-scaler));
  font-family: Pretendard;
  font-weight: 600;
  white-space: nowrap; /* 텍스트 줄바꿈 방지 */

  /* 2017:917 이상에서만 가로 기준 스케일링 적용 - 매우 완만하게 */
  @media (min-aspect-ratio: 2.2/1) {
    font-size: ${(32 / 1920) * 100}vw; /* 40에서 32로 완만하게 */
  }

  /* 가장 넓은 가로 비율 (3:1 이상) - 가장 작게 */
  @media (min-aspect-ratio: 3/1) {
    font-size: calc(1.4 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(25 / 1920) * 100}vw; /* 30에서 25로 완만하게 */
  }

  /* 넓은 가로 비율 (21:9 ~ 3:1) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    font-size: calc(1.6 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(28 / 1920) * 100}vw; /* 35에서 28로 완만하게 */
  }

  /* 중간 넓은 가로 비율 (2.2:1 ~ 21:9) */
  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) {
    font-size: calc(1.8 * var(--font-scaler));
  }

  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) and (min-aspect-ratio: 2.2/1) {
    font-size: ${(30 / 1920) * 100}vw; /* 40에서 30으로 완만하게 */
  }

  /* 약간 넓은 가로 비율 (2.0:1 ~ 2.2:1) - 가장 크게 */
  @media (min-aspect-ratio: 2/1) and (max-aspect-ratio: 2.2) {
    font-size: calc(2 * var(--font-scaler));
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    --font-scaler: ${rv(10)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 폰트 크기 보장 */
  @media (max-height: 900px) {
    font-size: max(calc(1.8 * var(--font-scaler)), 18px);
  }

  @media (max-height: 700px) {
    font-size: max(calc(1.8 * var(--font-scaler)), 15px);
  }

  @media (max-height: 500px) {
    font-size: max(calc(1.8 * var(--font-scaler)), 13px);
  }
`;

// 개별 날씨 데이터 아이템
const WeatherDataItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center; /* 중앙 정렬로 변경 */
  gap: ${r(24)};
  width: auto; /* 100% → auto로 변경하여 내용에 맞게 크기 조정 */
  padding: ${r(8)}; /* 패딩 추가 */
  box-sizing: border-box;

  /* 중간 세로 비율에서 각 아이템을 세로 배치로 변경 (620:917 ~ 457:917) */
  @media (max-aspect-ratio: 620/917) and (min-aspect-ratio: 457/917) {
    flex-direction: column;
    gap: ${r(12)};
    padding: ${r(6)};
  }

  /* 매우 좁은 세로 비율에서 각 아이템을 다시 가로 배치로 변경 (456:917 ~ 394:917) */
  @media (max-aspect-ratio: 456/917) and (min-aspect-ratio: 394/917) {
    flex-direction: row;
    gap: ${r(16)};
    padding: ${r(4)};
  }

  /* 가로가 매우 길고 세로가 짧은 화면에서 간격/패딩 조정 */
  @media (min-aspect-ratio: 21/9) {
    gap: ${r(18)};
    padding: ${r(6)};
  }

  @media (min-aspect-ratio: 32/9) {
    gap: ${r(15)};
    padding: ${r(5)};
  }

  @media (min-aspect-ratio: 48/9) {
    gap: ${r(12)};
    padding: ${r(4)};
  }

  /* 가장 넓은 가로 비율 (3:1 이상) - 가장 작게 */
  @media (min-aspect-ratio: 3/1) {
    gap: ${r(15)};
    padding: ${r(5)};
  }

  /* 넓은 가로 비율 (21:9 ~ 3:1) */
  @media (min-aspect-ratio: 21/9) and (max-aspect-ratio: 3/1) {
    gap: ${r(18)};
    padding: ${r(6)};
  }

  /* 중간 넓은 가로 비율 (2.2:1 ~ 21:9) */
  @media (min-aspect-ratio: 2.2) and (max-aspect-ratio: 21/9) {
    gap: ${r(24)};
    padding: ${r(8)};
  }

  /* 약간 넓은 가로 비율 (2.0:1 ~ 2.2:1) - 가장 크게 */
  @media (min-aspect-ratio: 2/1) and (max-aspect-ratio: 2.2) {
    gap: ${r(28)};
    padding: ${r(10)};
  }

  /* 세로가 매우 길고 좁은 화면에서 간격/패딩 증가 */
  @media (max-aspect-ratio: 9/21) {
    gap: ${r(35)};
    padding: ${r(12)};
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    gap: ${rv(12)};
    padding: ${rv(4)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 간격 보장 */
  @media (max-height: 900px) {
    gap: max(${r(24)}, 14px);
    padding: max(${r(8)}, 5px);
  }

  @media (max-height: 700px) {
    gap: max(${r(24)}, 12px);
    padding: max(${r(8)}, 4px);
  }

  @media (max-height: 500px) {
    gap: max(${r(24)}, 10px);
    padding: max(${r(8)}, 3px);
  }
`;

// 날씨 데이터 아이콘 컨테이너
const WeatherDataIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${r(8)};

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    gap: ${rv(4)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 간격 보장 */
  @media (max-height: 900px) {
    gap: max(${r(8)}, 5px);
  }

  @media (max-height: 700px) {
    gap: max(${r(8)}, 4px);
  }

  @media (max-height: 500px) {
    gap: max(${r(8)}, 3px);
  }
`;

// 날씨 데이터 값 컨테이너
const WeatherDataValueContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${r(4)};

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    gap: ${rv(2)};
  }

  /* 높이 기반 반응형 - 화면 높이가 낮을 때 간격 보장 */
  @media (max-height: 900px) {
    gap: max(${r(4)}, 2px);
  }

  @media (max-height: 700px) {
    gap: max(${r(4)}, 2px);
  }

  @media (max-height: 500px) {
    gap: max(${r(4)}, 1px);
  }
`;

// 백그라운드 선택 버튼
const BackgroundSelectorButton = styled.button`
  position: fixed;
  top: ${r(20)};
  right: ${r(20)};
  width: ${r(50)};
  height: ${r(50)};
  border-radius: 50%;
  border: 2px solid
    ${({ themeMode }) => getThemeColor("MainPanelOutline", themeMode)};
  background: ${({ themeMode }) =>
    themeMode === "dark" ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.7)"};
  color: ${({ themeMode }) => getThemeColor("mainColor", themeMode)};
  font-size: ${r(20)};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    transform: scale(1.1);
    background: ${({ themeMode }) =>
      themeMode === "dark" ? "rgba(0, 0, 0, 0.9)" : "rgba(255, 255, 255, 0.9)"};
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    top: ${rv(10)};
    right: ${rv(10)};
    width: ${rv(25)};
    height: ${rv(25)};
    font-size: ${rv(10)};
  }
`;

// 백그라운드 선택 팝업
const BackgroundSelectorPopup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${r(600)};
  max-height: ${r(500)};
  background: ${({ themeMode }) =>
    themeMode === "dark" ? "rgba(0, 0, 0, 0.9)" : "rgba(255, 255, 255, 0.9)"};
  border: 2px solid
    ${({ themeMode }) => getThemeColor("MainPanelOutline", themeMode)};
  border-radius: ${r(20)};
  padding: ${r(30)};
  backdrop-filter: blur(15px);
  z-index: 2000;
  overflow-y: auto;

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    width: ${rv(300)};
    max-height: ${rv(250)};
    padding: ${rv(15)};
    border-radius: ${rv(10)};
  }

  /* 모바일 대응 */
  @media (max-width: 768px) {
    width: 90vw;
    max-height: 70vh;
    padding: ${r(20)};
  }
`;

// 백그라운드 선택 팝업 제목
const BackgroundSelectorTitle = styled.h3`
  color: ${({ themeMode }) => getThemeColor("mainColor", themeMode)};
  font-size: ${r(24)};
  font-family: Pretendard;
  font-weight: 600;
  margin: 0 0 ${r(20)} 0;
  text-align: center;

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    font-size: ${rv(12)};
    margin: 0 0 ${rv(10)} 0;
  }
`;

// 백그라운드 이미지 그리드
const BackgroundImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${r(15)};
  margin-bottom: ${r(20)};

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${rv(7)};
    margin-bottom: ${rv(10)};
  }

  /* 작은 화면에서는 3열로 */
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${r(10)};
  }

  /* 매우 작은 화면에서는 2열로 */
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${r(8)};
  }
`;

// 백그라운드 이미지 미리보기
const BackgroundImagePreview = styled.img`
  width: 100%;
  height: ${r(80)};
  object-fit: cover;
  border-radius: ${r(10)};
  border: 3px solid
    ${({ isSelected, themeMode }) => (isSelected ? "#4CAF50" : "transparent")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    border-color: ${({ themeMode }) => getThemeColor("mainColor", themeMode)};
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    height: ${rv(40)};
    border-radius: ${rv(5)};
  }
`;

// 백그라운드 선택 팝업 닫기 버튼
const BackgroundSelectorCloseButton = styled.button`
  position: absolute;
  top: ${r(15)};
  right: ${r(15)};
  width: ${r(30)};
  height: ${r(30)};
  border: none;
  background: transparent;
  color: ${({ themeMode }) => getThemeColor("mainColor", themeMode)};
  font-size: ${r(20)};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ themeMode }) =>
      themeMode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)"};
  }

  /* 1:1 비율 이후 세로가 긴 경우 */
  @media (max-aspect-ratio: 1/1) {
    top: ${rv(7)};
    right: ${rv(7)};
    width: ${rv(15)};
    height: ${rv(15)};
    font-size: ${rv(10)};
  }
`;